import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MessageCircleQuestion } from "lucide-react";

export const faqs = [
  {
    question: "How is Reqqy different from just asking ChatGPT?",
    answer:
      "ChatGPT can help write text. Reqqy is built as a product workflow: it stores product memory, builds context snapshots, generates or reviews requirements, evaluates readiness, supports refinement, keeps versions, and exports into delivery-ready formats.",
  },
  {
    question: "Can I use Reqqy with just a rough idea?",
    answer:
      "Yes. You can start with a feature idea, a short product brief, or scattered notes. Reqqy uses the input plus available product and project context to create a structured first pass of requirements.",
  },
  {
    question: "Can I use Reqqy with an existing PRD?",
    answer:
      "Yes. Reqqy is equally useful for reviewing existing PRDs or requirement drafts. It can identify ambiguity, missing decisions, edge cases, weak acceptance criteria, and engineering questions before implementation starts.",
  },
  {
    question: "What does Reqqy return?",
    answer:
      "Depending on the workflow, Reqqy can return structured requirements, user stories, acceptance criteria, readiness evaluation, ambiguity notes, engineering questions, refinement outputs, version history, and export files.",
  },
  {
    question: "Does Reqqy replace product managers?",
    answer:
      "No. Reqqy is designed to support product judgment, not replace it. It handles repetitive requirement review and structure so teams can spend more time on prioritization, trade-offs, decisions, and stakeholder alignment.",
  },
  {
    question: "How does Reqqy fit into delivery tools?",
    answer:
      "Reqqy sits upstream of tools like Jira, Linear, Notion, and documentation systems. Today it exports tool-ready formats such as Markdown, JSON, CSV, Jira CSV, Linear CSV, and Notion-ready files. Native integrations can come later.",
  },
  {
    question: "What about privacy and sensitive product work?",
    answer:
      "Reqqy is designed for sensitive product context. It does not train models on customer content, supports temporary processing modes, and keeps context and version history visible so teams can understand what was used during a run.",
  },
  {
    question: "Can I try it before committing?",
    answer:
      "Yes. Reqqy has a free starting flow so you can test it with your own product ideas or PRD drafts before deciding whether it fits your workflow.",
  },
];

export function FAQSection() {
  return (
    <section className="py-14 md:py-20 relative overflow-hidden">
      <div className="absolute top-[30%] left-[20%] w-[300px] h-[300px] rounded-full bg-accent/[0.03] blur-[120px]" />

      <div className="container relative z-10 px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <MessageCircleQuestion className="w-3.5 h-3.5" />
            FAQ
          </div>
          <h2 className="text-2xl md:text-3xl font-sans font-bold mb-3">
            Frequently asked questions
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Everything you need to know about Reqqy
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="group rounded-lg glass border border-border/30 px-5 transition-all duration-300 hover:border-primary/15 data-[state=open]:border-primary/20"
              >
                <AccordionTrigger className="text-left text-sm hover:no-underline py-4 [&[data-state=open]>svg]:text-primary">
                  <span className="font-medium">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
